import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Languages, RotateCcw, Save, Search, Sparkles, X } from 'lucide-react';
import i18n, { applyOverrides } from '../i18n';
import { TranslationRow, isRowMissingPl } from './TranslationRow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// App screen labels live in the i18next bundle; document wording is applied server-side.
const SECTIONS = {
  app: { catalogue: '/i18n/catalogue', overrides: '/i18n/overrides', live: true },
  documents: { catalogue: '/i18n/doc-catalogue', overrides: '/i18n/doc-overrides', live: false },
};

const TranslationsSettings = () => {
  const { t } = useTranslation();
  const [section, setSection] = useState('app');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [confirmResetAll, setConfirmResetAll] = useState(false);
  const [autoTranslating, setAutoTranslating] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const cfg = SECTIONS[section];

  const loadCatalogue = useCallback(async (keepDrafts = false) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}${SECTIONS[section].catalogue}`);
      setRows(res.data.keys || []);
      if (!keepDrafts) setDrafts({});
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [section, t]);

  useEffect(() => { loadCatalogue(); }, [loadCatalogue]);

  const changeSection = (s) => { setSection(s); setFilter('all'); setDrafts({}); };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'overridden' && !(r.en_override != null || r.pl_override != null)) return false;
      if (filter === 'missing_pl' && !isRowMissingPl(r)) return false;
      if ((filter === 'email' || filter === 'detected') && r.group !== filter) return false;
      if (!term) return true;
      return r.key.toLowerCase().includes(term)
        || (r.en || '').toLowerCase().includes(term)
        || (r.pl || '').toLowerCase().includes(term);
    });
  }, [rows, search, filter]);

  const overriddenCount = rows.filter((r) => r.en_override != null || r.pl_override != null).length;
  const missingRows = useMemo(() => rows.filter(isRowMissingPl), [rows]);
  const pendingEdits = useMemo(() => {
    const byKey = Object.fromEntries(rows.map((r) => [r.key, r]));
    const out = { en: {}, pl: {} };
    Object.entries(drafts).forEach(([key, d]) => ['en', 'pl'].forEach((lang) => {
      if (d[lang] !== undefined && byKey[key] && d[lang] !== byKey[key][lang]) out[lang][key] = d[lang];
    }));
    return out;
  }, [drafts, rows]);
  const pendingCount = Object.keys(pendingEdits.en).length + Object.keys(pendingEdits.pl).length;

  const setDraft = (key, lang, value) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [lang]: value } }));
  };

  const clearDraft = (key, lang) => {
    setDrafts((prev) => {
      const next = { ...prev };
      if (next[key]) {
        const { [lang]: _, ...rest } = next[key];
        next[key] = rest;
        if (!Object.keys(next[key]).length) delete next[key];
      }
      return next;
    });
  };

  const applyLocal = (lang, key, value, isDefault) => {
    if (cfg.live) i18n.addResource(lang, 'translation', key, value);
    setRows((prev) => prev.map((r) => (
      r.key !== key ? r : { ...r, [lang]: value, [`${lang}_override`]: isDefault ? null : value }
    )));
  };

  const saveOne = async (row, lang) => {
    const draft = drafts[row.key]?.[lang];
    if (draft === undefined) return;
    const flag = `${row.key}-${lang}`;
    setSaving((prev) => ({ ...prev, [flag]: true }));
    try {
      await axios.put(`${API}${cfg.overrides}/${lang}`, { key: row.key, value: draft });
      applyLocal(lang, row.key, draft, (lang === 'en' ? row.en_default : row.pl_default) === draft);
      clearDraft(row.key, lang);
      toast.success(t('translationsEditor.toasts.saved', { key: row.key, lang: lang.toUpperCase() }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.saveFailed'));
    } finally {
      setSaving((prev) => ({ ...prev, [flag]: false }));
    }
  };

  const saveAll = async () => {
    setBulkSaving(true);
    try {
      for (const lang of ['en', 'pl']) {
        const edits = pendingEdits[lang];
        if (!Object.keys(edits).length) continue;
        await axios.post(`${API}${cfg.overrides}/${lang}/bulk`, { overrides: edits });
        if (cfg.live) Object.entries(edits).forEach(([k, v]) => i18n.addResource(lang, 'translation', k, v));
      }
      toast.success(t('translationsEditor.toasts.bulkSaved', { count: pendingCount }));
      await loadCatalogue();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.saveFailed'));
    } finally {
      setBulkSaving(false);
    }
  };

  const resetOne = async (row, lang) => {
    const flag = `${row.key}-${lang}`;
    setSaving((prev) => ({ ...prev, [flag]: true }));
    try {
      await axios.delete(`${API}${cfg.overrides}/${lang}/${encodeURIComponent(row.key)}`);
      applyLocal(lang, row.key, lang === 'en' ? row.en_default : row.pl_default, true);
      clearDraft(row.key, lang);
      toast.success(t('translationsEditor.toasts.reverted', { key: row.key, lang: lang.toUpperCase() }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.resetFailed'));
    } finally {
      setSaving((prev) => ({ ...prev, [flag]: false }));
    }
  };

  const dismissRow = async (row) => {
    try {
      await axios.post(`${API}/i18n/doc-detected/dismiss`, { key: row.key });
      setRows((prev) => prev.filter((r) => r.key !== row.key));
      toast.success(t('translationsEditor.toasts.dismissed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.saveFailed'));
    }
  };

  const setReviewed = async (row, reviewed) => {
    try {
      await axios.post(`${API}/i18n/reviewed`, { section, keys: [row.key], reviewed });
      setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, reviewed } : r)));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.saveFailed'));
    }
  };

  const resetAll = async () => {
    setConfirmResetAll(false);
    try {
      await axios.delete(`${API}${cfg.overrides}`);
      if (cfg.live) applyOverrides({ en: {}, pl: {} });
      toast.success(t('translationsEditor.toasts.resetAllDone'));
      await loadCatalogue();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('translationsEditor.toasts.resetFailed'));
    }
  };

  // AI suggestions go into drafts so the office reviews them before anything is saved.
  const suggestMissing = async () => {
    const missing = missingRows.filter((r) => (r.en || r.en_default || '').trim());
    if (missing.length === 0) {
      toast.info(t('translationsEditor.toasts.autoTranslateNothing'));
      return;
    }
    setAutoTranslating(true);
    const toastId = toast.loading(t('translationsEditor.toasts.autoTranslateStart', { count: missing.length }));
    const current = Object.fromEntries(missing.map((r) => [r.key, r.pl || '']));
    let filled = 0;
    let failedChunks = 0;
    const CHUNK = 50;
    for (let i = 0; i < missing.length; i += CHUNK) {
      const items = missing.slice(i, i + CHUNK).map((r) => ({ key: r.key, source: r.en || r.en_default }));
      try {
        const res = await axios.post(`${API}/i18n/translate-batch`, { target_lang: 'pl', source_lang: 'en', items });
        const translations = res.data.translations || {};
        setDrafts((prev) => {
          const next = { ...prev };
          Object.entries(translations).forEach(([k, v]) => { next[k] = { ...(next[k] || {}), pl: v }; });
          return next;
        });
        filled += Object.entries(translations).filter(([k, v]) => v !== current[k]).length;
      } catch (err) {
        failedChunks += 1;
      }
    }
    setAutoTranslating(false);
    if (filled > 0) {
      setFilter('missing_pl');
      toast.success(t('translationsEditor.toasts.suggested', { count: filled }), { id: toastId });
    } else {
      toast.error(t('translationsEditor.toasts.autoTranslateFailed'), { id: toastId });
    }
    if (failedChunks > 0) toast.warning(t('translationsEditor.toasts.chunksFailed', { count: failedChunks }));
  };

  return (
    <Card data-testid="translations-settings-card">
      <CardHeader>
        <div className="flex flex-wrap gap-3 items-start justify-between">
          <div className="min-w-0 flex-1">
            <CardTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5" />
              {t('translationsEditor.title')}
            </CardTitle>
            <CardDescription>
              {section === 'documents' ? t('translationsEditor.docsDescription') : t('translationsEditor.description')}
            </CardDescription>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Badge variant="outline" data-testid="translations-count-overridden">
              {t('translationsEditor.overridden', { count: overriddenCount })}
            </Badge>
            <Badge variant="outline" className="cursor-pointer" onClick={() => setFilter('missing_pl')}
              data-testid="translations-count-missing-pl">
              {t('translationsEditor.missingPl', { count: missingRows.length })}
            </Badge>
            <Button size="sm" variant="outline" onClick={suggestMissing}
              disabled={autoTranslating || missingRows.length === 0}
              title={t('translationsEditor.autoTranslateHint')} data-testid="translations-auto-translate">
              <Sparkles className="h-4 w-4 mr-1" />
              {autoTranslating ? t('translationsEditor.translating') : t('translationsEditor.autoTranslate')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConfirmResetAll(true)}
              disabled={overriddenCount === 0} data-testid="translations-reset-all">
              <RotateCcw className="h-4 w-4 mr-1" />
              {t('translationsEditor.resetAll')}
            </Button>
          </div>
        </div>
        <Tabs value={section} onValueChange={changeSection} className="mt-3">
          <TabsList data-testid="translations-section-tabs">
            <TabsTrigger value="app" data-testid="translations-section-app">{t('translationsEditor.sections.app')}</TabsTrigger>
            <TabsTrigger value="documents" data-testid="translations-section-documents">{t('translationsEditor.sections.documents')}</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input placeholder={t('translationsEditor.searchPlaceholder')} value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-8" data-testid="translations-search-input" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[240px]" data-testid="translations-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('translationsEditor.filter.all')}</SelectItem>
              <SelectItem value="overridden">{t('translationsEditor.filter.overridden')}</SelectItem>
              <SelectItem value="missing_pl" data-testid="translations-filter-missing">{t('translationsEditor.filter.missingPl')}</SelectItem>
              {section === 'documents' && <SelectItem value="email" data-testid="translations-filter-email">{t('translationsEditor.filter.email')}</SelectItem>}
              {section === 'documents' && <SelectItem value="detected" data-testid="translations-filter-detected">{t('translationsEditor.filter.detected')}</SelectItem>}
            </SelectContent>
          </Select>
          {pendingCount > 0 && (
            <div className="flex gap-2">
              <Button size="sm" onClick={saveAll} disabled={bulkSaving} data-testid="translations-save-all">
                <Save className="h-4 w-4 mr-1" />
                {t('translationsEditor.saveAll', { count: pendingCount })}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDrafts({})} disabled={bulkSaving} data-testid="translations-discard-all">
                <X className="h-4 w-4 mr-1" />
                {t('translationsEditor.discardAll')}
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">{t('translationsEditor.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center" data-testid="translations-empty">{t('translationsEditor.empty')}</div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <div className="max-h-[70vh] overflow-auto">
              <table className="w-full text-sm" data-testid="translations-table">
                <thead className="sticky top-0 bg-slate-50 border-b z-10">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                    <th className="px-3 py-2 w-1/4">{t('translationsEditor.columns.key')}</th>
                    <th className="px-3 py-2 w-1/3">{t('translationsEditor.columns.english')}</th>
                    <th className="px-3 py-2 w-1/3">{t('translationsEditor.columns.polski')}</th>
                    <th className="px-3 py-2 w-24 text-right">{t('translationsEditor.columns.reset')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 500).map((row) => (
                    <TranslationRow key={row.key} row={row} draft={drafts[row.key]} saving={saving}
                      onChange={setDraft} onSave={saveOne} onReset={resetOne}
                      onDismiss={section === 'documents' ? dismissRow : null} onReviewed={setReviewed} />
                  ))}
                </tbody>
              </table>
              {filtered.length > 500 && (
                <div className="text-xs text-slate-500 text-center py-2 bg-slate-50 border-t">
                  {t('translationsEditor.showingFirst', { count: 500, total: filtered.length })}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmResetAll} onOpenChange={setConfirmResetAll}>
        <AlertDialogContent data-testid="translations-reset-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('translationsEditor.resetAll')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('translationsEditor.resetAllConfirm', { count: overriddenCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="translations-reset-all-cancel">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={resetAll} data-testid="translations-reset-all-confirm">
              {t('translationsEditor.resetAll')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default TranslationsSettings;
